
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { canAccessCallScripts } from '@/services/subscriptions/access';
import { Filter, CheckCircle2 } from 'lucide-react';

interface TemplateTabsProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  children: React.ReactNode;
}

const TemplateTabs: React.FC<TemplateTabsProps> = ({
  activeTab,
  onTabChange,
  children,
}) => {
  const [canAccessCalls, setCanAccessCalls] = useState(true);

  useEffect(() => {
    checkCallAccess();
  }, []);

  const checkCallAccess = async () => {
    const access = await canAccessCallScripts();
    setCanAccessCalls(access);
  };

  const statusLabels: Record<string, string> = {
    'new': 'New',
    'contacted': 'Contacted',
    'qualified': 'Qualified',
    'negotiation': 'Negotiation'
  };

  const getActiveStatusLabel = () => {
    if (activeTab === 'all' || activeTab === 'default') return null;
    return statusLabels[activeTab] || null;
  };

  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <TabsList className="flex-none w-full sm:w-auto bg-muted p-1 rounded-lg">
          <TabsTrigger 
            value="all" 
            className="text-sm px-4 py-2 min-h-[44px]"
          >
            All
          </TabsTrigger>
          <TabsTrigger 
            value="default" 
            className="text-sm px-4 py-2 min-h-[44px]"
          >
            Default
          </TabsTrigger>
        </TabsList>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full sm:w-auto h-10 min-h-[44px]">
              <Filter className="h-4 w-4 mr-2" />
              {getActiveStatusLabel() || 'By Status'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={() => onTabChange('new')}>
              {activeTab === 'new' && <CheckCircle2 className="h-4 w-4 mr-2" />}
              {activeTab !== 'new' && <span className="w-4 mr-2" />}
              New
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onTabChange('contacted')}>
              {activeTab === 'contacted' && <CheckCircle2 className="h-4 w-4 mr-2" />}
              {activeTab !== 'contacted' && <span className="w-4 mr-2" />}
              Contacted
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onTabChange('qualified')}>
              {activeTab === 'qualified' && <CheckCircle2 className="h-4 w-4 mr-2" />}
              {activeTab !== 'qualified' && <span className="w-4 mr-2" />}
              Qualified
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onTabChange('negotiation')}>
              {activeTab === 'negotiation' && <CheckCircle2 className="h-4 w-4 mr-2" />}
              {activeTab !== 'negotiation' && <span className="w-4 mr-2" />}
              Negotiation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <TabsContent value={activeTab} className="mt-0">
        {children}
      </TabsContent>
    </Tabs>
  );
};

export default TemplateTabs;
